import React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  SearchableTagInput,
  type Option
} from '@/components/ui/searchable-tag-input';
import { useAppSearchParams } from '@/hooks/use-app-search-params';
import { showToast } from '@/components/ui/show-toast';
import { useUpdateMapData } from '@/services/mapdata/queries';
import type { MapCategory, MapData } from '@/services/types';

type ExpertiseFormProps = {
  mapCategories: MapCategory[];
  mapData: MapData[];
  organisationId: string;
  componentType?: 'verify' | 'edit';
};

export default function ExpertiseForm(props: ExpertiseFormProps) {
  const { mapCategories, mapData, organisationId, componentType = 'verify' } = props;
  const params = useAppSearchParams();  
  // State for each category's selected options
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Option[]>>({});
  
  // Mutation for updating map data
  const { mutate: updateMapData, isPending: isUpdating } = useUpdateMapData(
    organisationId || '',
    () => {
      showToast('success', 'Expertise information updated successfully');
      params.set({ view: 'media' });
    },
    () => {
      showToast('error', 'Failed to update expertise information. Please try again.');
    }
  );

  // Helper function to get options for a category by ID
  const getCategoryOptions = (categoryId: string): Option[] => {
    const category = mapCategories.find(cat => cat.id === categoryId);
    if (!category) return [];
    
    return category.tags.map(tag => ({
      id: tag,
      label: tag
    }));
  };

  const getSelectedOptionsForCategory = (categoryId: string): Option[] => {
    return selectedOptions[categoryId] || [];
  };

  const updateSelectedOptionsForCategory = (categoryId: string, options: Option[]) => {
    setSelectedOptions(prev => ({
      ...prev,
      [categoryId]: options
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organisationId) {
      showToast('error', 'Organization data not available');
      return;
    }

    // Prepare submission data using the new API format
    const updateData = Object.entries(selectedOptions)
      .filter(([_, options]) => options.length > 0)
      .map(([categoryId, options]) => ({
        category: categoryId,
        organisation: organisationId,
        tags: options.map(opt => opt.label)
      }));

    if (updateData.length === 0) {
      showToast('error', 'Please select at least one option');
      return;
    }

    // Update map data using the mutation
    updateMapData(updateData);
  };

  // Prefill selected options from existing map data
  useEffect(() => {
    if (mapData && mapData.length > 0) {
      const initialSelectedOptions: Record<string, Option[]> = {};
      
      mapData.forEach((dataItem) => {
        const options = dataItem.tags.map(tag => ({
          id: tag,
          label: tag
        }));
        
        initialSelectedOptions[dataItem.category] = options;
      });
      
      setSelectedOptions(initialSelectedOptions);
    }
  }, [mapData]);

  return (
    <div className={`max-w-3xl p-6 font-grotesk ${componentType === 'verify' ? 'mx-auto' : ''}`}>
      <div className='mb-8'>
        <h1 className='text-2xl font-bold'>Expertise & Niche Information</h1>
        <p className='mt-2 text-[#545454]'>
          You can select up to 10 of the most relevant options in these
          categories:
        </p>
      </div>

      <form onSubmit={handleSubmit} className='space-y-8'>
        {mapCategories.map((category) => (
          <SearchableTagInput
            key={category.id}
            label={category.name}
            options={getCategoryOptions(category.id)}
            selectedOptions={getSelectedOptionsForCategory(category.id)}
            onChange={(options) => updateSelectedOptionsForCategory(category.id, options)}
            maxSelections={10}
            placeholder='Search'
          />
        ))}

        <div className={`flex pt-4 ${componentType === 'edit' ? 'justify-end' : 'justify-between'}`}>
          { componentType === 'verify' && <Button
            variant='outline'
            type='button'
            onClick={() => params.set({ view: 'contact-info' })}
          >
            Back
          </Button>} 
          <Button type='submit' disabled={isUpdating}>
            {isUpdating ? 'Saving...' : componentType === 'verify' ? 'Next' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  );
}
